import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getErrorDetail } from '@/utils/apiError';
import { cn } from '@/utils/cn';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Tabs } from '@/components/UI/Tabs';
import { Alert } from '@/components/UI/Alert';
import { Chip } from '@/components/UI/Chip';
import { Button, buttonVariants } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import {
  useGetAgentQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useListModelsQuery,
  useListToolsQuery,
  useListScreensQuery,
  useGetParameterCatalogueQuery,
  usePreviewAgentPromptMutation,
  AgentVersion,
  PromptPreview,
} from '@/redux/internal-services/agentsApi';
import { Select } from '@/components/UI/Select';

import {
  AgentFormValues,
  agentSchema,
  emptyAgent,
  AgentTypeBadge,
  Modal,
  SNAP_FIELDS,
  KNOWN_MODULES,
  UNREACHABLE_MODULES,
  screenMeta,
  snapshot,
  makeVersion,
} from '@/components/Agent/agentShared';
import OverviewTab from '@/components/Agent/OverviewTab';
import InstructionsTab from '@/components/Agent/InstructionsTab';
import ToolsTab from '@/components/Agent/ToolsTab';
import ScreensTab from '@/components/Agent/ScreensTab';
import WorkflowTab from '@/components/Agent/WorkflowTab';
import { VersionsTab, SaveVersionModal, ViewVersionModal } from '@/components/Agent/VersionsTab';
import TestPanel from '@/components/Agent/TestPanel';

// ── types ──────────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'instructions' | 'tools' | 'screens' | 'advanced' | 'workflow' | 'test' | 'versions';
type ModalState =
  | null
  | { kind: 'saveVersion' }
  | { kind: 'viewVersion'; version: AgentVersion }
  | { kind: 'promptPreview' };

// ── AgentConfigPage ────────────────────────────────────────────────────────────

const AgentConfigPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const isEdit = Boolean(agentId && agentId !== 'new');
  const navigate = useNavigate();

  const { data: existing } = useGetAgentQuery(agentId as string, { skip: !isEdit });
  const { data: models } = useListModelsQuery();
  const { data: allTools } = useListToolsQuery();
  const { data: screens } = useListScreensQuery();
  const { data: paramCat } = useGetParameterCatalogueQuery();
  const [createAgent, { isLoading: creating }] = useCreateAgentMutation();
  const [updateAgent, { isLoading: updating }] = useUpdateAgentMutation();

  const [tab, setTab] = useState<Tab>('overview');
  const [modal, setModal] = useState<ModalState>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [serverError, setServerError] = useState('');
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [previewTools, setPreviewTools] = useState<PromptPreview['tools']>([]);
  const [previewUsage, setPreviewUsage] = useState<PromptPreview['usage'] | null>(null);
  // The modal renders ONE scrollable document — prompt then tools, in the order the model gets
  // them — so it can be read and copied as a whole. The chips scroll to a section rather than
  // swapping panes, which would break select-all.
  const previewScrollRef = useRef<HTMLPreElement | null>(null);
  const previewToolsRef = useRef<HTMLSpanElement | null>(null);
  const previewPromptRef = useRef<HTMLSpanElement | null>(null);
  const [previewCopied, setPreviewCopied] = useState(false);
  const [previewModule, setPreviewModule] = useState('');
  const [previewError, setPreviewError] = useState<string | null>(null);
  // true when the preview was opened from one screen's own card (the module is fixed — no reason
  // to offer a dropdown to switch to a different screen); false from the Instructions tab's
  // general "Preview compiled prompt" button, where picking any module is the whole point.
  const [previewLocked, setPreviewLocked] = useState(false);
  const [previewAgentPrompt, { isLoading: previewLoading }] = usePreviewAgentPromptMutation();

  // ── form ────────────────────────────────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const methods = useForm<AgentFormValues>({
    resolver: zodResolver(agentSchema) as any,
    defaultValues: emptyAgent,
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isDirty, errors },
  } = methods;

  // Populate form when server data loads
  useEffect(() => {
    if (existing) reset({ ...emptyAgent, ...existing });
  }, [existing, reset]);

  // Seed first model when creating a new agent
  useEffect(() => {
    if (!isEdit && models && models.length > 0 && !getValues('models.text')) {
      setValue('models.text', models[0].name, { shouldDirty: false });
    }
  }, [models, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // A NEW agent starts with the cross-cutting core tools (knowledge-base search, screen context, calendar,
  // notifications) already selected. Seeded once: if the admin removes them in the Tools tab they stay
  // removed, because the ref stops this re-adding them when the catalogue refetches. The backend never
  // injects tools an agent does not own, so what is selected here is exactly what the agent gets.
  const coreSeeded = useRef(false);
  useEffect(() => {
    if (isEdit || coreSeeded.current || !allTools || !screens) return;
    coreSeeded.current = true;
    const core = new Set(screens.core_tools);
    const coreRefs = allTools
      .filter((t) => t.name && core.has(t.name))
      .map((t) => ({
        tool_id: t.tool_id,
        name: t.name,
        tool_name: t.tool_name || t.name,
        description: t.description,
        requires_confirmation: t.requires_confirmation ?? false,
        argument_schema: t.argument_schema,
        toolkit_type: t.toolkit_type,
        toolkit_name: t.toolkit_name,
        action_card_template: t.action_card_template,
      }));
    if (coreRefs.length && !(getValues('tools') || []).length) {
      setValue('tools', coreRefs, { shouldDirty: false });
    }
  }, [allTools, screens, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── derived state ────────────────────────────────────────────────────────────

  const agentType = watch('agent_type');
  const agentModelText = watch('models.text');
  const agentVersions = watch('versions') || [];
  const publishedId = watch('published_version_id');
  const isWorkflow = agentType === 'workflow';

  const sortedVersions = useMemo(
    () => [...agentVersions].sort((a, b) => b.n - a.n),
    [agentVersions],
  );
  const latestVersion = agentVersions.length
    ? agentVersions[agentVersions.length - 1]
    : null;
  const publishedVersion = agentVersions.find((v) => v.id === publishedId) || null;

  const dirtyVsLatest = latestVersion
    ? JSON.stringify(snapshot(getValues() as Record<string, unknown>)) !== JSON.stringify(latestVersion.snapshot)
    : (watch('system_prompts') || []).length > 0;

  const paramGroups = paramCat?.groups || {};

  const tabs: Array<{ k: Tab; l: string }> = [
    { k: 'overview', l: 'Overview' },
    { k: 'instructions', l: 'Instructions' },
    { k: 'tools', l: 'Tools' },
    { k: 'screens', l: 'Modules' },
    { k: 'advanced', l: 'Advanced' },
    ...(isEdit && isWorkflow ? [{ k: 'workflow' as Tab, l: 'Workflow' }] : []),
    ...(isEdit ? [{ k: 'test' as Tab, l: 'Test' }, { k: 'versions' as Tab, l: 'History' }] : []),
  ];

  // ── actions ──────────────────────────────────────────────────────────────────

  const persist = async (values: AgentFormValues): Promise<boolean> => {
    setServerError('');
    // Stamp order from array index so backend sort matches the UI order.
    const payload: AgentFormValues = {
      ...values,
      system_prompts: (values.system_prompts || []).map((b, i) => ({ ...b, order: i })),
    };
    try {
      if (isEdit) {
        await updateAgent({ id: agentId as string, body: payload }).unwrap();
      } else {
        await createAgent(payload).unwrap();
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
      return true;
    } catch (err: unknown) {
      setServerError(getErrorDetail(err) ?? 'Failed to save agent.');
      return false;
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    const ok = await persist(values as AgentFormValues);
    if (ok && !isEdit) navigate('/platform/agents');
  });

  // Every module an admin could preview as the active screen — same union ScreensTab.tsx computes
  // (known modules, minus unreachable ones, plus any extra key already in tool_scope_overrides.screens).
  const previewModules = useMemo(() => {
    const extra = Object.keys(watch('tool_scope_overrides')?.screens ?? {}).filter((m) => !KNOWN_MODULES.includes(m));
    return [...KNOWN_MODULES, ...extra].filter((m) => !UNREACHABLE_MODULES.has(m));
  }, [watch('tool_scope_overrides')]); // eslint-disable-line react-hooks/exhaustive-deps

  const runPreview = async (module: string) => {
    if (!agentId) return;
    setPreviewError(null);
    try {
      const result = await previewAgentPrompt({
        agent_id: agentId,
        module: module || undefined,
        tool_scope_overrides: watch('tool_scope_overrides') ?? undefined,
      }).unwrap();
      setPreviewText(result.system_prompt);
      setPreviewTools(result.tools ?? []);
      setPreviewUsage(result.usage ?? null);
    } catch (err) {
      setPreviewText(null);
      setPreviewTools([]);
      setPreviewUsage(null);
      setPreviewError(getErrorDetail(err) ?? 'Failed to compile the preview.');
    }
  };

  // Scroll the one preview document to a section instead of swapping panes, so select-all and
  // copy still take the whole thing.
  const scrollPreviewTo = (ref: React.RefObject<HTMLSpanElement | null>) => {
    const box = previewScrollRef.current;
    const target = ref.current;
    if (!box || !target) return;
    // offsetTop is measured from the nearest positioned ancestor, which is not this <pre> — use
    // the rects so the maths is right wherever the modal sits.
    const top = target.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
    box.scrollTo({ top, behavior: 'smooth' });
  };

  const copyPreview = async () => {
    const tools = previewTools.length
      ? `

${'='.repeat(60)}
TOOLS (${previewTools.length}) — sent alongside the prompt
${'='.repeat(60)}
${JSON.stringify(previewTools, null, 2)}`
      : '';
    try {
      await navigator.clipboard.writeText(`${previewText ?? ''}${tools}`);
      setPreviewCopied(true);
      setTimeout(() => setPreviewCopied(false), 1500);
    } catch {
      setPreviewCopied(false);
    }
  };

  const handlePreview = () => {
    setPreviewLocked(false);
    setModal({ kind: 'promptPreview' });
    void runPreview(previewModule);
  };

  const changePreviewModule = (module: string) => {
    setPreviewModule(module);
    void runPreview(module);
  };

  // Entry point from a Screens-tab card's own "Preview prompt on this screen" button — opens the
  // same compiled-prompt modal, pre-selected to that card's module, so an admin can see the full
  // Global + every module's General + this screen's own Specific without hunting for the
  // Instructions tab's preview and picking the module from its dropdown.
  const previewScreen = (module: string) => {
    setPreviewModule(module);
    setPreviewLocked(true);
    setModal({ kind: 'promptPreview' });
    void runPreview(module);
  };

  const exportAgent = () => {
    const values = getValues();
    const blob = new Blob([JSON.stringify(values, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(values.agent_name || 'agent').replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importAgent = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const imported = JSON.parse(await file.text());
      reset({ ...emptyAgent, ...imported });
      setServerError('');
    } catch {
      setServerError('Import failed — file is not valid agent JSON.');
    }
    e.target.value = '';
  };

  // ── version management ────────────────────────────────────────────────────────

  const saveVersion = async (message: string, publish: boolean) => {
    const v = makeVersion(getValues() as Record<string, unknown>, message);
    const currentVersions = getValues('versions') || [];
    const nextVersions = [...currentVersions, v];
    setValue('versions', nextVersions, { shouldDirty: true });
    if (publish) setValue('published_version_id', v.id, { shouldDirty: true });
    setModal(null);
    if (isEdit) await methods.handleSubmit((v) => persist(v as AgentFormValues))();
  };

  const restoreVersion = (v: AgentVersion) => {
    const snap = v.snapshot as Partial<AgentFormValues>;
    SNAP_FIELDS.forEach((k) => {
      if (k in snap) setValue(k as keyof AgentFormValues, snap[k as keyof typeof snap] as never, { shouldDirty: true });
    });
    setModal(null);
    setTab('instructions');
  };

  const publishVersion = async (v: AgentVersion) => {
    setValue('published_version_id', v.id, { shouldDirty: true });
    setModal(null);
    if (isEdit) await methods.handleSubmit((v) => persist(v as AgentFormValues))();
  };

  // ── render ───────────────────────────────────────────────────────────────────

  const agentName = watch('agent_name');
  const nextVersionN = agentVersions.length
    ? Math.max(...agentVersions.map((v) => v.n)) + 1
    : 1;

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} noValidate className="h-full">
        <PageShell
          maxWidth="max-w-4xl"
          header={
            <PageHeader
              back={
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  onClick={() => navigate('/platform/agents')}
                >
                  ← Agents
                </Button>
              }
              title={
                <span className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 truncate">{isEdit ? (agentName || 'Edit Agent') : 'New Agent'}</span>
                  <AgentTypeBadge type={agentType} />
                </span>
              }
              subtitle={
                <>
                  <span className="font-mono">{agentModelText}</span>
                  {publishedVersion ? (
                    <> · <span className="text-success-text">v{publishedVersion.n} live</span></>
                  ) : isEdit ? (
                    ' · no published version'
                  ) : null}
                  {isEdit && dirtyVsLatest ? (
                    <span className="text-warning-text"> · unsaved version</span>
                  ) : null}
                  {isEdit && agentId && (
                    <span
                      className="ml-1 cursor-copy font-mono text-ui-text3 hover:text-ui-text2"
                      title="Click to copy agent ID"
                      onClick={() => navigator.clipboard.writeText(agentId)}
                    > · {agentId}</span>
                  )}
                </>
              }
              actions={
                <>
                  {savedFlash && <span className="text-xs font-medium text-success-text">✓ Saved</span>}
                  {isDirty && !savedFlash && (
                    <span className="text-xs font-medium text-warning-text">Unsaved changes</span>
                  )}

                  <Button type="button" variant="outline" size="sm" onClick={exportAgent}>
                    Export
                  </Button>
                  <label className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'cursor-pointer')}>
                    Import
                    <input type="file" accept="application/json" className="hidden" onChange={importAgent} />
                  </label>

                  <Button type="submit" size="sm" disabled={creating || updating}>
                    {creating || updating ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Agent'}
                  </Button>
                </>
              }
              toolbar={
                <Tabs
                  label="Agent configuration"
                  tabs={tabs.map((t) => ({ id: t.k, label: t.l }))}
                  value={tab}
                  onChange={(id) => setTab(id as Tab)}
                />
              }
            />
          }
        >

          {/* Validation / server errors */}
          {(serverError || errors.agent_name || errors.models?.text) && (
            <Alert tone="danger" className="mb-4 px-4 py-3 text-sm">
              {serverError ||
                errors.agent_name?.message ||
                errors.models?.text?.message}
            </Alert>
          )}

          {tab === 'overview' && (
            <OverviewTab
              isEdit={isEdit}
              models={models || []}
              paramGroups={paramGroups}
            />
          )}

          {tab === 'instructions' && (
            <InstructionsTab
              isEdit={isEdit}
              onPreview={handlePreview}
              previewing={previewLoading}
            />
          )}

          {tab === 'tools' && <ToolsTab allTools={allTools || []} />}

          {tab === 'screens' && <ScreensTab onPreviewModule={isEdit ? previewScreen : undefined} />}

          {tab === 'advanced' && (
            <AdvancedTab paramGroups={paramGroups} />
          )}

          {tab === 'workflow' && isEdit && isWorkflow && (
            <WorkflowTab agentId={agentId as string} triggerType={watch('trigger_type')} />
          )}

          {tab === 'test' && isEdit && (
            <TestPanel agentId={agentId as string} agentName={agentName} />
          )}

          {tab === 'versions' && isEdit && (
            <VersionsTab
              versions={sortedVersions}
              publishedId={publishedId}
              dirty={dirtyVsLatest}
              latestN={latestVersion?.n}
              onSaveVersion={() => setModal({ kind: 'saveVersion' })}
              onView={(v) => setModal({ kind: 'viewVersion', version: v })}
            />
          )}
        </PageShell>

        {/* ── Modals ── */}
        {modal?.kind === 'saveVersion' && (
          <SaveVersionModal
            nextN={nextVersionN}
            onClose={() => setModal(null)}
            onSave={saveVersion}
          />
        )}
        {modal?.kind === 'viewVersion' && (
          <ViewVersionModal
            version={modal.version}
            current={getValues()}
            isPublished={modal.version.id === publishedId}
            onClose={() => setModal(null)}
            onRestore={() => restoreVersion(modal.version)}
            onPublish={() => publishVersion(modal.version)}
          />
        )}
        {modal?.kind === 'promptPreview' && (
          <Modal
            wide
            title={`Compiled system prompt — ${previewModule ? screenMeta(previewModule).label : 'Global only'}`}
            onClose={() => setModal(null)}
          >
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {previewLocked ? (
                <span className="text-xs text-ui-text3">
                  Showing what this screen gets: Global + every module's General + this screen's own Specific, and the tool set scoped to it.
                </span>
              ) : (
                <>
                  <span className="text-xs text-ui-text3">Preview as:</span>
                  <Select
                    value={previewModule}
                    onChange={(e) => changePreviewModule(e.target.value)}
                    wrapperClassName="w-56"
                  >
                    <option value="">— Global only —</option>
                    {previewModules.map((m) => (
                      <option key={m} value={m}>{screenMeta(m).label}</option>
                    ))}
                  </Select>
                </>
              )}
              {isDirty && (
                <span className="text-xs text-warning-text">
                  Showing the last saved version — Save Changes to preview unsaved edits.
                </span>
              )}
            </div>
            {previewUsage && !previewError && (
              <div className="mb-3 flex flex-wrap items-center gap-1.5">
                <Chip onClick={() => scrollPreviewTo(previewPromptRef)}>
                  {`Prompt · ${previewUsage.prompt_chars.toLocaleString()} chars`}
                </Chip>
                <Chip onClick={() => scrollPreviewTo(previewToolsRef)}>
                  {`Tools (${previewUsage.tool_count}) · ${previewUsage.tool_chars.toLocaleString()} chars`}
                </Chip>
                <span className="text-xs text-ui-text3">
                  {`≈ ${Math.round(previewUsage.total_chars / 4).toLocaleString()} tokens sent on this screen`}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  className="ml-auto"
                  onClick={copyPreview}
                >
                  {previewCopied ? 'Copied' : 'Copy all'}
                </Button>
              </div>
            )}
            {previewError ? (
              <Alert tone="danger">{previewError}</Alert>
            ) : (
              <pre
                ref={previewScrollRef}
                className="max-h-[50dvh] overflow-y-auto whitespace-pre-wrap rounded-control border border-ui-border bg-ui-subtle p-3 font-mono text-xs leading-relaxed text-ui-text"
              >
                {previewLoading ? 'Loading…' : (
                  <>
                    <span ref={previewPromptRef}>{previewText}</span>
                    {previewTools.length > 0 && (
                      <>
                        <span ref={previewToolsRef}>
                          {`

${'='.repeat(60)}
TOOLS (${previewTools.length}) — sent alongside the prompt
${'='.repeat(60)}
`}
                          {JSON.stringify(previewTools, null, 2)}
                        </span>
                      </>
                    )}
                  </>
                )}
              </pre>
            )}
          </Modal>
        )}
      </form>
    </FormProvider>
  );
};

// ── AdvancedTab (inline — only 2 static cards) ─────────────────────────────────

import { useFormContext } from 'react-hook-form';
import { DescriptorField, SectionCard } from '@/components/Agent/agentShared';
import { ParameterDescriptor } from '@/redux/internal-services/agentsApi';

const AdvancedTab: React.FC<{ paramGroups: Record<string, ParameterDescriptor[]> }> = ({ paramGroups }) => {
  const { watch, setValue } = useFormContext<AgentFormValues>();

  const modelsParams = watch('models') as Record<string, unknown> || {};
  const voiceParams = watch('voice') || {};

  const setModelField = (key: string, value: unknown) =>
    setValue('models', { ...modelsParams, [key]: value } as AgentFormValues['models'], { shouldDirty: true });

  const setVoiceField = (key: string, value: unknown) =>
    setValue('voice', { ...voiceParams, [key]: value }, { shouldDirty: true });

  const modelDescriptors = paramGroups['models'] || [];
  const voiceDescriptors = paramGroups['voice'] || [];

  return (
    <>
      <SectionCard
        title="Models"
        desc="Realtime model override for interactive/hands-free mode. Leave blank to use the platform default."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {modelDescriptors.map((d) => (
            <DescriptorField
              key={d.key}
              d={d}
              value={modelsParams[d.key] ?? d.default}
              onChange={(v) => setModelField(d.key, v)}
            />
          ))}
          {modelDescriptors.length === 0 && (
            <EmptyState variant="inline" title="No model overrides defined." />
          )}
        </div>
      </SectionCard>

      <SectionCard title="Voice / STT / TTS">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {voiceDescriptors.map((d) => (
            <DescriptorField
              key={d.key}
              d={d}
              value={voiceParams[d.key] ?? d.default}
              onChange={(v) => setVoiceField(d.key, v)}
            />
          ))}
          {voiceDescriptors.length === 0 && (
            <EmptyState variant="inline" title="No voice parameters defined." />
          )}
        </div>
      </SectionCard>
    </>
  );
};

export default AgentConfigPage;
